import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewChild,} from '@angular/core';
import {Widget} from '@ispm/widgets/decorator/widget.decorator';
import {IWidgetItem} from '@ispm/widgets/interfaces/widget-item.interface';
import {IdynamicFormItem} from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import {FileProcessorOptions, FileUploadService} from '@ispm/shared/services/file-upload-service';
import Quill, {DeltaStatic, QuillOptionsStatic} from 'quill';
import {isEmpty} from 'lodash';
import {ModalManagementService} from "@ispm/layout/services/modal-management.service";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {PdfService} from "@ispm/test/services/pdf.service";
import {Attachment, AttachmentPayload} from "@ispm/shared/components/rich-edit-box/Attachment";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {RichTextUtils} from "@ispm/shared/classes/RichTextUtils";
import {RichTextConstants} from "@ispm/shared/classes/RichTextConstants";

import {get as lodashGet, set} from 'lodash';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
interface RichTextData {
  text: string;
  attachments?: Array<any>
}

@Widget('RichEditBox')
@Component({
  selector: 'ispm-rich-edit-box',
  templateUrl: './rich-edit-box.component.html',
  styleUrls: ['./rich-edit-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RichEditBoxComponent implements IWidgetItem {
  @ViewChild('attachmentUploadInput', {static: true}) attachmentUploadInput;
  @ViewChild('editBoxWrapper', {static: true}) editBoxWrapper;

  public readOnly: boolean = false;
  public editable: boolean = true;
  public attachmentsList: Attachment[] = [];
  public widgetUpdateValueCallback: (value) => void;
  private fileProcessorOptions: FileProcessorOptions = {};
  private text: string = '';
  private _widgetConfig: IdynamicFormItem;
  private quillEditor: Quill;
  private enableModalManagement: boolean = false;
  public readOnlyContent: SafeHtml;
  private changesCount: number = 0;

  constructor(private uploadService: FileUploadService, private changeDetectorRef: ChangeDetectorRef, private modalManagementService: ModalManagementService,
    private notificationMessageService: NotificationMessageService, private notificationMessage: NOTIFICATION_MESSAGE,
   private workflowService: WorkflowService, private pdfService: PdfService, private domSanitizer: DomSanitizer) {
  }

  get attachments(): AttachmentPayload[] {
    return this.attachmentsList.filter(attachment => !attachment.isFailed).map(attachment => {
      return attachment.getAttachmentPayload();
    });
  }

  set attachments(attachmentPayload: AttachmentPayload[]) {
    this.attachmentsList = [];
    attachmentPayload.forEach(payload => {
      let attachment;
      if(this.readOnly){
        attachment = new Attachment(payload.name, false, false, payload.ref, payload.referenceId, payload.name);

      } else {
        attachment = new Attachment(new File([], payload.name), false, false, payload.ref, payload.referenceId, payload.name);
      }
      this.attachmentsList.push(attachment);
    });
  }

  get widgetConfig(): IdynamicFormItem {
    return this._widgetConfig;
  }

  set widgetConfig(config) {
    this._widgetConfig = config;
    if (this._widgetConfig.widgetPayload) {
      this.readOnly = this._widgetConfig.widgetPayload.readOnly;
      this.editable = this._widgetConfig.widgetPayload.editMode;
      this.enableModalManagement = this._widgetConfig.widgetPayload.enableModalManagement;
      const discardDataOnClickOutside = this._widgetConfig.widgetPayload.discardDataOnClickOutside;
      this.triggerModalManagement(this.modalManagementService.setDiscardDataOnClickOutside.bind(this.modalManagementService), discardDataOnClickOutside)


    }
    this.createAndWrapQuillElement();
    this.changeDetectorRef.markForCheck();
  }

  get widgetData(): RichTextData {
    return {
      text: this.text,
      attachments: this.attachments
    };
  }

  set widgetData(widgetData: RichTextData) {
    this.text = widgetData?.text ?? '';
    this.readOnlyContent = this.domSanitizer.bypassSecurityTrustHtml(this.text);
    if (isEmpty(widgetData.attachments)) {
      set(widgetData,'attachments', this.attachments);
    } else {
      this.attachments = lodashGet(widgetData,'attachments');
    }

    if (!this.readOnly && this.quillEditor.getLength() == 1) {
      this.quillEditor.pasteHTML(this.text);
    }
    this.changeDetectorRef.markForCheck();
  }

  get htmlValue(): string {
    return this.quillEditor?.root?.innerHTML ?? '';
  }

  set htmlValue(text: string) {
    if (this.quillEditor?.root) {
      this.quillEditor.root.innerHTML = text;
    }
  }

  onSelectAttachment($event: any) {
    const file = $event.target.files[0];
    const attachment: Attachment = new Attachment(file);
    this.uploadAttachment(attachment);
  }

  uploadAttachment(attachment: Attachment) {
    const options = {
      checksumAlgorithm: this._widgetConfig.widgetPayload.checksumAlgorithm,
      fileProcessorOptions: this.fileProcessorOptions,
      processorType: this._widgetConfig.widgetPayload.processorType
    };
    attachment.startUpload();
    const index = this.attachmentsList.indexOf(attachment, 0);
    if (index === -1) {
      this.attachmentsList.push(attachment);
    }
    this.triggerModalManagement(this.modalManagementService.disableApproveButton.bind(this.modalManagementService))

    this.uploadService.uploadFormData(attachment.file as File, this._widgetConfig.widgetPayload.storageName, options).subscribe(
      (data) => {
        attachment.setAttachmentPayload(data.body as AttachmentPayload);
        this.triggerModalManagement(this.modalManagementService.enableApproveButton.bind(this.modalManagementService));
        attachment.isLoading = false;
        this.dataChanged();
        this.changeDetectorRef.markForCheck();
      },
      (error => {
        this.triggerModalManagement(this.modalManagementService.enableApproveButton.bind(this.modalManagementService));
        this.notificationMessageService.error(this.notificationMessage.FILE_UPLOAD_FAILED);
        attachment.setUploadFailed();
        this.changeDetectorRef.markForCheck();
      })
    );
  }

  removeAttachment(attachment: Attachment) {
    const index = this.attachmentsList.indexOf(attachment, 0);
    if (index > -1) {
      this.attachmentsList.splice(index, 1);
      this.attachmentUploadInput.nativeElement.value = '';
      this.dataChanged();
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * in order to support DLS element CSS styles,
   * we create HTML structure like this:
   * <dls-rich-editbox>
   *   <div class='dls-rich-editbox'>
   *     <div class='ql-toolbar'>
   *       <dls-editbox-header>
   *         ... toolbar buttons
   *
   */
  private createAndWrapQuillElement(): void {
    if (!this.editBoxWrapper || this.readOnly) {
      return;
    }

    this.editBoxWrapper.nativeElement.innerHTML = '';

    const dlsClassElement = document.createElement('div');
    dlsClassElement.setAttribute('class', 'dls-rich-editbox');

    const dlsRichEditboxElement = document.createElement('dls-rich-editbox');
    dlsRichEditboxElement.appendChild(dlsClassElement);
    const quillElement = document.createElement('div');
    dlsClassElement.appendChild(quillElement);

    this.editBoxWrapper.nativeElement.appendChild(dlsRichEditboxElement);
    this.createQuill(quillElement);

    const dlsEditboxHeaderElement = document.createElement('dls-editbox-header');
    const toolBarElement = dlsClassElement.firstChild;
    dlsEditboxHeaderElement.append(...Array.from(toolBarElement.childNodes));
    toolBarElement.appendChild(dlsEditboxHeaderElement);
  }

  private createQuill(quillElement: Element): void {
    const toolbarOptions = this._widgetConfig.widgetPayload.toolBarOptions;
    const quillOptions: QuillOptionsStatic = {
      modules: {
        toolbar: toolbarOptions
      },
      placeholder: this._widgetConfig.widgetPayload.placeholder,
      theme: 'snow',
      readOnly: !this.editable,
    };
    this.quillEditor = new Quill(quillElement, quillOptions);
    this.addHttpPrefix();
    this.setAttachmentButton();
    this.setRichTextButtonClasses();

    this.quillEditor.on('text-change', () => this.dataChanged());
    this.quillEditor.on('text-change', (delta) => this.autoLinkOnTyping(delta));
    this.quillEditor.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => this.autoLinkOnPasting(node, delta))
  }

  private addHttpPrefix() {
    let Link = Quill.import('formats/link');
    Link.sanitize = function (url) {
      url = (url.indexOf(RichTextConstants.PROTOCOL) === -1) ? RichTextConstants.HTTP + RichTextConstants.PROTOCOL + url : url;
      return url;
    }
  }

  private setRichTextButtonClasses(): void {
    const qlButtons = this.editBoxWrapper.nativeElement.querySelectorAll('button');
    for (const eachButton of qlButtons) {
      eachButton.classList.add('rich-text-button');
    }
  }

  private dataChanged(): void {
    if (this.changesCount !== 0) {
      this.triggerModalManagement(this.modalManagementService.dataIsChanged.bind(this.modalManagementService));
    }
    if(!this.readOnly || this.editable){
      this.changesCount++;
    }
    this.widgetUpdateValueCallback.apply(this, [{text: this.htmlValue, attachments: this.attachments}]);
  }

  private autoLinkOnPasting(node: any, delta: DeltaStatic) {
    if (typeof (node.data) !== 'string') return;
    if(this.changesCount === 0 && (!this.readOnly || this.editable)) return delta;
    const matchHttpProtocol = node.data.match(RichTextConstants.HttpRegex) || node.data.match(RichTextConstants.HttpsRegex);
    if (matchHttpProtocol && matchHttpProtocol.length > 0) {
      let ops = [];
      let str = node.data;
      matchHttpProtocol.forEach(function (match) {
        const split = str.split(match);
        const beforeLink = split.shift();
        ops.push({insert: beforeLink});
        ops.push({insert: match, attributes: {link: match}});
        str = split.join(match);
      });
      ops.push({insert: str});
      delta.ops = ops;
    }

    return delta;
  }

  private autoLinkOnTyping(delta): void {
    if (delta.ops.length === 2 && delta.ops[0].retain && RichTextUtils.isWhitespace(delta.ops[1].insert)) {
      const endRetain = delta.ops[0].retain;
      const text = this.quillEditor.getText().substr(0, endRetain);
      const matchHttpProtocol = text.match(RichTextConstants.HttpRegex) || text.match(RichTextConstants.HttpsRegex);
      if (matchHttpProtocol !== null) {
        const url = matchHttpProtocol[0];
        let ops = [];
        if (endRetain > url.length) {
          ops.push({retain: endRetain - url.length});
        }

        ops = ops.concat([
          {delete: url.length},
          {insert: url, attributes: {link: url}}
        ]);
        delta.ops = ops;
        this.quillEditor.updateContents(delta);
      }
    }
  }

  private setAttachmentButton(): void {
    const qlFileButton = this.editBoxWrapper.nativeElement.querySelector('.ql-file');
    if (qlFileButton) {
      qlFileButton.classList.add('button__icon', 'fa', 'fa-paperclip');
      qlFileButton.addEventListener('click', () => {
        this.attachmentUploadInput.nativeElement.click();
      });
    }
  }

  download(attachment: Attachment) {
    if (!attachment.isAvailable()) {
      return;
    }
    return this.workflowService.getReportData(attachment.ref)
      .subscribe(result => {
        this.pdfService.downloadPDF(result, attachment.getAttachmentName(), attachment.ref);
      },err=>{
        this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_GETTING_DATA_FROM_SERVER);
      }).add(() => {
            this.changeDetectorRef.markForCheck();
          }
        );
  }

  private triggerModalManagement(callback: any, ...args: any) {
    if (this.enableModalManagement && callback && typeof callback === 'function') {
      callback(...args);
    }
  }
}
